import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import {
  Card,
  Button,
  Spinner,
  Alert,
  Badge,
  Form,
  Row,
  Col,
  Container,
  ListGroup,
  Modal,
  Dropdown,
  OverlayTrigger,
  Tooltip,
  Collapse,
  ButtonGroup,
} from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalendarAlt,
  faEdit,
  faTrashAlt,
  faSave,
  faTimes,
  faArrowLeft,
  faUsers,
  faCheck,
  faMapMarkerAlt,
  faClock,
  faUserCircle,
  faSignal,
  faSpinner,
  faTasks,
  faPlus,
  faLink,
  faUserCheck,
  faCircle,
  faCircleCheck,
  faUserClock,
  faUserXmark,
  faChevronDown,
  faChevronUp,
  faList,
  faExclamationTriangle,
  faStream,
} from "@fortawesome/free-solid-svg-icons";
import LotusIcon from "../components/LotusIcon";
import QuillEditor from "./QuillEditor";
import FileAttachments from "./FileAttachments";
import {
  getMeetingById,
  updateMeeting,
  deleteMeeting,
  updateMeetingNotes,
  getMembers,
  getAllUsers,
  getCommitteeById,
  getAllTasks,
} from "../api/api";
import { useAuth } from "../context/AuthContext";
import toast from "../utils/toastUtils";
import {
  initSocket,
  joinMeeting,
  leaveMeeting,
  sendNotesUpdate,
  reconnectSocket,
  getSocketStatus,
} from "../utils/socket";
import { getTasks, updateTask, getTask } from "../api/tasks";
import "../components/QuillEditor.css";
import TaskEditModal from "./TaskEditModal";

const MeetingDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isEditMode = searchParams.get("edit") === "true";
  const { currentUser, isAuthenticated } = useAuth();

  const [meeting, setMeeting] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(isEditMode);
  const [formData, setFormData] = useState({
    title: "",
    date: "",
    committee: "",
    location: "",
  });

  // For real-time collaboration
  const [notes, setNotes] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [activeUsers, setActiveUsers] = useState([]);
  const [connectionStatus, setConnectionStatus] = useState("connecting");
  const [lastChangeInfo, setLastChangeInfo] = useState(null);
  const [isUpdateRecent, setIsUpdateRecent] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Add after the existing state variables
  const [availableTasks, setAvailableTasks] = useState([]);
  const [attachedTasks, setAttachedTasks] = useState([]);
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [showTaskModal, setShowTaskModal] = useState(false);

  // Attendance tracking
  const [attendanceList, setAttendanceList] = useState([]);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  // Add a new state variable for the attendance collapse
  const [showAttendance, setShowAttendance] = useState(false);

  // Add state for the task detail modal
  const [showTaskDetailModal, setShowTaskDetailModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [loadingTaskDetails, setLoadingTaskDetails] = useState(false);

  // Add new state variables for agenda items
  const [agendaItems, setAgendaItems] = useState([]);
  const [agendaItemBeingEdited, setAgendaItemBeingEdited] = useState(null);
  const [newItemText, setNewItemText] = useState("");

  // Add state for task edit modal
  const [showTaskEditModal, setShowTaskEditModal] = useState(false);
  const [taskToEdit, setTaskToEdit] = useState(null);

  const socketRef = useRef(null);
  const notesTimeoutRef = useRef(null);
  const recentUpdateTimeoutRef = useRef(null);
  const saveTimerRef = useRef(null);

  // Update the useEffect for socket initialization with retry logic
  useEffect(() => {
    if (!isAuthenticated || !id) return;

    let socketInitRetryTimeout = null;

    const initializeSocket = () => {
      // Initialize socket
      const socket = initSocket();
      socketRef.current = socket;

      // Set connection status based on socket state
      socket.on("connect", () => {
        setConnectionStatus("connected");
        toast.success("Connected to collaboration server");

        // Join the meeting room
        if (currentUser) {
          joinMeeting(id, {
            id: currentUser._id,
            name: currentUser.name || "Anonymous",
          });
        }
      });

      socket.on("connect_error", (error) => {
        setConnectionStatus("error");
        toast.error(`Connection error: ${error.message || "Unknown error"}`);

        // Schedule a retry after 5 seconds if not already reconnecting
        if (!socket.io.reconnecting && !socketInitRetryTimeout) {
          socketInitRetryTimeout = setTimeout(() => {
            console.log("[SOCKET] Retrying socket initialization...");
            socket.connect();
          }, 5000);
        }
      });

      socket.on("disconnect", (reason) => {
        setConnectionStatus("disconnected");
        if (reason !== "io client disconnect") {
          toast.warn(`Disconnected: ${reason}. Attempting to reconnect...`);
        }
      });

      socket.on("reconnect", (attemptNumber) => {
        setConnectionStatus("connected");
        toast.success(`Reconnected after ${attemptNumber} attempts`);

        // Rejoin the meeting
        if (currentUser) {
          joinMeeting(
            id,
            {
              id: currentUser._id,
              name: currentUser.name || "Anonymous",
            },
            true
          );
        }
      });

      socket.on("reconnect_error", () => {
        setConnectionStatus("error");
      });

      socket.on("reconnect_failed", () => {
        setConnectionStatus("error");
        toast.error(
          "Failed to reconnect after multiple attempts. Please try manual reconnection."
        );
      });

      // If we're already connected, join the meeting
      if (socket.connected && currentUser) {
        setConnectionStatus("connected");
        joinMeeting(id, {
          id: currentUser._id,
          name: currentUser.name || "Anonymous",
        });
      }
    };

    // Initial socket initialization
    initializeSocket();

    // Cleanup function
    return () => {
      if (socketInitRetryTimeout) {
        clearTimeout(socketInitRetryTimeout);
      }

      if (currentUser && id && socketRef.current) {
        try {
          leaveMeeting(id, currentUser._id || socketRef.current.id);
        } catch (err) {
          console.error("Error leaving meeting:", err);
        }
      }

      // Remove event listeners
      if (socketRef.current) {
        socketRef.current.off("connect");
        socketRef.current.off("connect_error");
        socketRef.current.off("disconnect");
        socketRef.current.off("reconnect");
        socketRef.current.off("reconnect_error");
        socketRef.current.off("reconnect_failed");
      }
    };
  }, [id, isAuthenticated, currentUser]);

  // Load meeting details
  useEffect(() => {
    const loadMeeting = async () => {
      if (!id) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const data = await getMeetingById(id);
        console.log("Meeting data loaded:", data);
        setMeeting(data);
        setNotes(data.notes || "");

        // Set attached tasks if they exist in the meeting data
        if (data.attachedTasks && data.attachedTasks.length > 0) {
          setAttachedTasks(data.attachedTasks);
        }

        setFormData({
          title: data.title || "",
          date: data.date
            ? new Date(data.date).toISOString().split("T")[0]
            : "",
          committee: data.committee || "",
          location: data.location || "",
        });

        setLastSaved(data.lastNotesUpdate || data.updatedAt || data.createdAt);

        // Load users for the committee after we have the meeting data
        if (data.committee) {
          console.log("Loading users for committee:", data.committee);
          loadUsers(data.committee, data.attendance || []);
        }

        setLoading(false);
      } catch (err) {
        console.error("Error loading meeting:", err);
        setError("Failed to load meeting details");
        setLoading(false);
      }
    };

    loadMeeting();
  }, [id]);

  // Load users for the committee and set up attendance
  const loadUsers = async (committee, existingAttendance = []) => {
    setLoadingUsers(true);
    try {
      console.log("Fetching members for committee:", committee);

      // Use the getMembers API function from the API module
      let params = {};
      if (committee && committee !== "SASComm") {
        params.committee = committee;
      }

      const users = await getMembers(params);
      console.log("Users loaded:", users);
      setAvailableUsers(users);

      // Create attendance list from committee members
      const newAttendanceList = users.map((user) => {
        // Check if user already has attendance status
        const existingRecord = existingAttendance.find(
          (record) => record.userId === user._id
        );

        if (existingRecord) {
          return existingRecord; // Keep existing status if available
        }

        // Otherwise create a new record with default status
        return {
          userId: user._id,
          name: user.name,
          status: "absent", // Default status
        };
      });

      console.log("New attendance list:", newAttendanceList);
      setAttendanceList(newAttendanceList);

      // If we have an existing meeting, update it with the new attendance list
      if (meeting && meeting._id) {
        // Only update if the attendance list has actually changed
        if (
          JSON.stringify(meeting.attendance) !==
          JSON.stringify(newAttendanceList)
        ) {
          console.log("Updating meeting with new attendance list");
          const updatedMeeting = await updateMeeting(meeting._id, {
            ...meeting,
            attendance: newAttendanceList,
          });
          setMeeting(updatedMeeting);
        }
      }
    } catch (err) {
      console.error("Error loading users:", err);
      toast.error("Failed to load users. Please try again later.");
    } finally {
      setLoadingUsers(false);
    }
  };

  // Get status icon and color
  const getStatusIcon = (status) => {
    switch (status) {
      case "present":
        return (
          <FontAwesomeIcon icon={faCircleCheck} className="text-success" />
        );
      case "late":
        return <FontAwesomeIcon icon={faUserClock} className="text-warning" />;
      case "absent":
        return <FontAwesomeIcon icon={faUserXmark} className="text-danger" />;
      default:
        return <FontAwesomeIcon icon={faCircle} className="text-secondary" />;
    }
  };

  // Safely handle attendance status change
  const handleAttendanceStatusChange = async (attendeeId, newStatus) => {
    if (!attendeeId) {
      console.error("Invalid attendee ID");
      return;
    }

    try {
      // Create updated attendance list
      const updatedAttendance = attendanceList.map((attendee) =>
        attendee.userId === attendeeId
          ? { ...attendee, status: newStatus }
          : attendee
      );

      // Update local state immediately for responsive UI
      setAttendanceList(updatedAttendance);

      // Update the meeting with new attendance
      const updatedMeeting = await updateMeeting(id, {
        ...meeting,
        attendance: updatedAttendance,
      });

      // Update meeting state with server response
      setMeeting(updatedMeeting);
      toast.success("Attendance updated");
    } catch (err) {
      console.error("Error updating attendance:", err);
      toast.error("Failed to update attendance");
    }
  };

  // Load available tasks
  useEffect(() => {
    const loadTasks = async () => {
      if (!isAuthenticated || !meeting?._id) return;

      try {
        setLoadingTasks(true);

        // Get all tasks
        const allTasks = await getTasks();
        setAvailableTasks(allTasks);

        // Get tasks related to this meeting (either through attachedTasks or relatedTo)
        const meetingTasks = await getTasks(null, meeting._id);

        // Combine tasks from both sources (attachedTasks in meeting object and tasks with relatedTo pointing to this meeting)
        let combinedAttachedTasks = [...meetingTasks];

        // Add tasks from meeting.attachedTasks that aren't already in the meetingTasks list
        if (meeting.attachedTasks?.length > 0) {
          const attachedTaskIds = meeting.attachedTasks.map((task) =>
            typeof task === "object" ? task._id : task
          );

          // Find tasks from allTasks that are in the attachedTaskIds list but not in meetingTasks
          const additionalTasks = allTasks.filter(
            (task) =>
              attachedTaskIds.includes(task._id) &&
              !meetingTasks.some((mt) => mt._id === task._id)
          );

          combinedAttachedTasks = [
            ...combinedAttachedTasks,
            ...additionalTasks,
          ];
        }

        console.log("Combined attached tasks:", combinedAttachedTasks);
        setAttachedTasks(combinedAttachedTasks);
      } catch (err) {
        console.error("Error loading tasks:", err);
        toast.error("Failed to load tasks");
      } finally {
        setLoadingTasks(false);
      }
    };

    if (meeting) {
      loadTasks();
    }
  }, [meeting, isAuthenticated]);

  // Subscribe to real-time updates
  useEffect(() => {
    if (!id || !currentUser || !socketRef.current) return;

    // Subscribe to notes updates
    const socket = socketRef.current;

    const handleNotesUpdate = (data) => {
      console.log("RECEIVED UPDATE:", data);

      // Skip updates from this user - let the local state handle these
      if (data.userId === currentUser?._id) {
        console.log("Skipping our own update received from server");
        return;
      }

      console.log(
        "Applying update from another user:",
        data.content.substring(0, 50) + "..."
      );

      // Update the notes state directly
      setNotes(data.content);

      // Update last saved time
      setLastSaved(data.timestamp || new Date());

      // Update last change info
      setLastChangeInfo({
        time: new Date(data.timestamp || new Date()),
        user: data.userName || "another user",
      });

      // Clear any existing timeout
      if (recentUpdateTimeoutRef.current) {
        clearTimeout(recentUpdateTimeoutRef.current);
      }

      // Set the update as recent (will show a visual indicator)
      setIsUpdateRecent(true);

      // Clear the recent update status after 2 seconds
      recentUpdateTimeoutRef.current = setTimeout(() => {
        setIsUpdateRecent(false);
      }, 2000);
    };

    // Subscribe to active users
    const handleActiveUsers = (data) => {
      console.log("Active users update:", data);
      if (Array.isArray(data.users)) {
        setActiveUsers(
          data.users.filter(
            (user) => user.userId !== (currentUser._id || "self")
          )
        );
      } else if (Array.isArray(data)) {
        setActiveUsers(
          data.filter((user) => user.userId !== (currentUser._id || "self"))
        );
      }
    };

    // Clean up existing listeners first to prevent duplicates
    socket.off("notes-update");
    socket.off("active-users");

    // Add our listeners
    socket.on("notes-update", handleNotesUpdate);
    socket.on("active-users", handleActiveUsers);

    console.log("Subscribed to notes updates and active users");

    // Return cleanup function
    return () => {
      console.log("Cleaning up socket listeners");
      socket.off("notes-update", handleNotesUpdate);
      socket.off("active-users", handleActiveUsers);

      // Clear any pending timers
      if (notesTimeoutRef.current) {
        clearTimeout(notesTimeoutRef.current);
      }
      if (recentUpdateTimeoutRef.current) {
        clearTimeout(recentUpdateTimeoutRef.current);
      }
    };
  }, [id, currentUser]);

  // Form input handlers
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  // Save meeting changes
  const handleSave = async () => {
    try {
      setIsSaving(true);
      setError(null);

      // Form validation
      if (!formData.title || !formData.date || !formData.committee) {
        setError("Please fill in all required fields");
        setIsSaving(false);
        return;
      }

      // Format date properly
      const updatedMeeting = {
        ...meeting,
        title: formData.title,
        date: new Date(formData.date).toISOString(),
        committee: formData.committee,
        location: formData.location,
      };

      await updateMeeting(id, updatedMeeting);
      setMeeting(updatedMeeting);
      setIsEditing(false);
      toast.success("Meeting updated successfully");
    } catch (err) {
      console.error("Error saving meeting:", err);
      setError("Failed to save meeting changes");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (
      window.confirm(
        "Are you sure you want to delete this meeting? This action cannot be undone."
      )
    ) {
      try {
        setIsDeleting(true);
        await deleteMeeting(id);
        toast.success("Meeting deleted successfully");
        navigate("/meetings");
      } catch (err) {
        console.error("Error deleting meeting:", err);
        toast.error("Failed to delete meeting");
        setIsDeleting(false);
        setShowDeleteModal(false);
      }
    }
  };

  // Notes editing handlers
  const handleNotesChange = (value) => {
    // First ensure we have a socket connection
    const socket = socketRef.current;
    if (!socket || !socket.connected) {
      console.warn(
        "Socket not connected, updates may not be sent in real-time"
      );

      // Show a toast notification only if this is the first disconnected update
      if (connectionStatus === "connected") {
        setConnectionStatus("disconnected");
        toast.warn(
          "Connection to server lost. Changes will be saved but not synchronized in real-time."
        );
      }
    }

    // Update local state immediately - the value contains the full HTML with formatting
    setNotes(value);

    // Update last change info
    const timestamp = new Date();
    setLastChangeInfo({
      time: timestamp,
      user: currentUser?.name || "you",
    });

    // Clear any existing timeout
    if (notesTimeoutRef.current) {
      clearTimeout(notesTimeoutRef.current);
    }

    // Use a variable delay based on typing speed
    // This helps avoid sending too many small updates that could cause cursor jumps
    const delay = value.length > 500 ? 500 : 300;

    // Send update with minimal delay
    notesTimeoutRef.current = setTimeout(() => {
      // Send update to server with the formatted HTML content
      const updateSent = sendNotesUpdate(id, {
        meetingId: id,
        content: value, // This includes all formatting (background, colors)
        userId: currentUser?._id || "anonymous",
        userName: currentUser?.name || "Anonymous",
        timestamp: timestamp,
      });

      // If we failed to send the update due to disconnection, still save to database
      if (!updateSent) {
        console.warn(
          "Could not send update in real-time, falling back to direct save"
        );
      }

      // Always save to database regardless of socket status - preserve full HTML formatting
      autoSaveNotes(value);

      // Update last saved indicator
      setLastSaved(timestamp);
    }, delay);
  };

  const autoSaveNotes = async (notesContent) => {
    try {
      setIsSaving(true);
      // Save the full HTML content with all formatting
      await updateMeetingNotes(id, notesContent);
      setLastSaved(new Date());
    } catch (err) {
      console.error("Error auto-saving notes:", err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleManualSaveNotes = async () => {
    try {
      setIsSaving(true);
      // Ensure we're saving the complete formatted HTML
      await updateMeetingNotes(id, notes);
      setLastSaved(new Date());
    } catch (err) {
      console.error("Error saving notes:", err);
      toast.error("Failed to save notes");
    } finally {
      setIsSaving(false);
    }
  };

  const formatLastSaved = () => {
    if (!lastSaved) return "Never saved";

    const date = new Date(lastSaved);
    const now = new Date();
    const diffMs = now - date;
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHour = Math.floor(diffMin / 60);

    if (diffSec < 60) {
      return "Just now";
    } else if (diffMin < 60) {
      return `${diffMin} minute${diffMin === 1 ? "" : "s"} ago`;
    } else if (diffHour < 24) {
      return `${diffHour} hour${diffHour === 1 ? "" : "s"} ago`;
    } else {
      return date.toLocaleDateString() + " at " + date.toLocaleTimeString();
    }
  };

  // Quill editor configuration
  const quillModules = useMemo(
    () => ({
      // We'll let the QuillEditor component handle its own toolbar
      // to avoid duplicating toolbars
    }),
    []
  );

  const quillFormats = useMemo(
    () => [
      "header",
      "bold",
      "italic",
      "underline",
      "strike",
      "list",
      "bullet",
      "ordered",
      "link",
      "image",
      "color",
      "background",
    ],
    []
  );

  // Function to get a badge color based on committee name
  const getCommitteeBadge = (committee) => {
    // Handle both string and object committee structure
    const committeeName =
      typeof committee === "object" ? committee.name : committee;

    const badgeMap = {
      PR: "danger",
      Operations: "info",
      Internal: "success",
      External: "warning",
      SASComm: "primary",
      Other: "secondary",
    };

    return badgeMap[committeeName] || "secondary";
  };

  // Add new function to handle attaching a task
  const handleAttachTask = async (taskId) => {
    try {
      // Find the task
      const taskToAttach = availableTasks.find((task) => task._id === taskId);
      if (!taskToAttach) return;

      // Update the task to relate it to this meeting
      await updateTask(taskId, {
        relatedTo: {
          type: "meeting",
          id: meeting._id,
          name: meeting.title,
        },
      });

      // Update the meeting with the new attached task
      const updatedMeeting = await updateMeeting(meeting._id, {
        attachedTasks: [...(meeting.attachedTasks || []), taskId],
      });

      // Update local state
      setMeeting(updatedMeeting);
      setAttachedTasks((prev) => [...prev, taskToAttach]);

      toast.success("Task attached successfully");
    } catch (err) {
      console.error("Error attaching task:", err);
      toast.error("Failed to attach task");
    }
  };

  // Add function to handle removing a task
  const handleRemoveTask = async (taskId) => {
    try {
      // Update the task to remove the meeting relationship
      await updateTask(taskId, {
        relatedTo: null,
      });

      // Update the meeting to remove the task
      const updatedMeeting = await updateMeeting(meeting._id, {
        attachedTasks: (meeting.attachedTasks || []).filter(
          (id) => id !== taskId
        ),
      });

      // Update local state
      setMeeting(updatedMeeting);
      setAttachedTasks((prev) => prev.filter((task) => task._id !== taskId));

      toast.success("Task removed successfully");
    } catch (err) {
      console.error("Error removing task:", err);
      toast.error("Failed to remove task");
    }
  };

  // Update the connection status display
  const renderConnectionStatus = () => {
    switch (connectionStatus) {
      case "connected":
        return (
          <span className="connection-status connected">
            <FontAwesomeIcon icon={faCircle} className="status-icon" />{" "}
            Connected
          </span>
        );
      case "connecting":
        return (
          <span className="connection-status connecting">
            <FontAwesomeIcon icon={faSpinner} spin className="status-icon" />{" "}
            Connecting...
          </span>
        );
      case "disconnected":
        return (
          <span className="connection-status disconnected">
            <FontAwesomeIcon icon={faCircle} className="status-icon" />{" "}
            Disconnected
            <Button
              variant="link"
              size="sm"
              className="reconnect-btn ms-2"
              onClick={handleReconnect}
              title="Try to reconnect"
            >
              <FontAwesomeIcon icon={faSignal} />
            </Button>
          </span>
        );
      case "error":
        return (
          <span className="connection-status error">
            <FontAwesomeIcon icon={faCircle} className="status-icon" />{" "}
            Connection Error
            <Button
              variant="link"
              size="sm"
              className="reconnect-btn ms-2"
              onClick={handleReconnect}
              title="Try to reconnect"
            >
              <FontAwesomeIcon icon={faSignal} />
            </Button>
          </span>
        );
      default:
        return null;
    }
  };

  // Add a function to handle manual reconnection attempts
  const handleReconnect = () => {
    setConnectionStatus("connecting");
    const success = reconnectSocket();

    if (success) {
      // Wait for the connection to be established (or fail)
      setTimeout(() => {
        const status = getSocketStatus();
        setConnectionStatus(status === "connected" ? "connected" : "error");

        // If connected, rejoin the meeting
        if (status === "connected" && currentUser && id) {
          joinMeeting(
            id,
            {
              id: currentUser._id,
              name: currentUser.name || "Anonymous",
            },
            true
          );

          toast.success("Reconnected to collaboration server");
        } else {
          toast.error("Failed to reconnect. Please try again later.");
        }
      }, 2000);
    } else {
      toast.error("Cannot reconnect - socket not initialized");
      setConnectionStatus("error");
    }
  };

  // Add a function to handle opening a task modal
  const handleOpenTaskDetail = async (taskId) => {
    setLoadingTaskDetails(true);
    setShowTaskDetailModal(true);

    try {
      const fetchedTask = await getTask(taskId);
      setSelectedTask(fetchedTask);
    } catch (error) {
      console.error("Error fetching task details:", error);
      toast.error("Failed to load task details");
    } finally {
      setLoadingTaskDetails(false);
    }
  };

  // Function to get priority badge color for tasks
  const getPriorityBadgeColor = (priority) => {
    switch (priority) {
      case "High":
        return "danger";
      case "Medium":
        return "warning";
      case "Low":
        return "success";
      default:
        return "secondary";
    }
  };

  // Function to get status badge color for tasks
  const getStatusBadgeColor = (status) => {
    switch (status) {
      case "To Do":
        return "secondary";
      case "In Progress":
        return "info";
      case "Done":
        return "success";
      default:
        return "secondary";
    }
  };

  // Format task date for display
  const formatTaskDate = (dateString) => {
    if (!dateString) return "No due date";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Function to handle editing a task
  const handleEditTask = (task) => {
    setTaskToEdit(task);
    setShowTaskEditModal(true);
    setShowTaskDetailModal(false); // Close the detail modal
  };

  // Function to handle task update after editing
  const handleTaskUpdated = (updatedTask) => {
    // Update the task in the attachedTasks array
    const updatedTasks = attachedTasks.map((task) =>
      task._id === updatedTask._id ? updatedTask : task
    );
    setAttachedTasks(updatedTasks);

    // Close the edit modal
    setShowTaskEditModal(false);
    toast.success("Task updated successfully");
  };

  if (loading && !meeting) {
    return (
      <Container>
        <div className="text-center py-5 lotus-loader">
          <div className="lotus-petals">
            <LotusIcon size={32} />
          </div>
          <p className="mt-3">Loading meeting details...</p>
        </div>
      </Container>
    );
  }

  if (error || !meeting) {
    return (
      <Alert variant="danger">
        {error || "Meeting not found"}
        <Button
          variant="outline-primary"
          size="sm"
          className="ms-3"
          onClick={() => navigate("/meetings")}
        >
          Back to Meetings
        </Button>
      </Alert>
    );
  }

  const formatDate = (dateString) => {
    const options = {
      year: "numeric",
      month: "long",
      day: "numeric",
    };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const getCommitteeBadgeComponent = (committee) => {
    let badgeColor = "secondary";

    switch (committee) {
      case "PR":
        badgeColor = "danger";
        break;
      case "Operations":
        badgeColor = "info";
        break;
      case "Internal":
        badgeColor = "success";
        break;
      case "External":
        badgeColor = "warning";
        break;
      case "SASComm":
        badgeColor = "primary";
        break;
      default:
        badgeColor = "secondary";
    }

    return (
      <Badge bg={badgeColor} className="sasc-badge">
        {committee}
      </Badge>
    );
  };

  // Render edit form
  if (isEditing) {
    return (
      <div>
        <h1 className="mb-4">Edit Meeting</h1>

        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Card>
          <Card.Header className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0">Meeting Details</h5>
            <div>
              <Button
                variant="primary"
                className="me-2"
                onClick={handleSave}
                disabled={isSaving}
              >
                <FontAwesomeIcon icon={faSave} className="me-1" />
                {isSaving ? "Saving..." : "Save"}
              </Button>
              <Button
                variant="outline-secondary"
                onClick={() => {
                  setIsEditing(false);
                  // Reset form data to original meeting data
                  setFormData({
                    title: meeting.title,
                    date: meeting.date
                      ? new Date(meeting.date).toISOString().split("T")[0]
                      : "",
                    committee: meeting.committee,
                    location: meeting.location || "",
                  });
                }}
              >
                <FontAwesomeIcon icon={faTimes} className="me-1" />
                Cancel
              </Button>
            </div>
          </Card.Header>

          <Card.Body>
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>Meeting Title*</Form.Label>
                <Form.Control
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  required
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Date*</Form.Label>
                <Form.Control
                  type="date"
                  name="date"
                  value={formData.date}
                  onChange={handleChange}
                  required
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Location</Form.Label>
                <Form.Control
                  type="text"
                  name="location"
                  value={formData.location}
                  onChange={handleChange}
                  placeholder="Enter meeting location"
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Committee*</Form.Label>
                <Form.Select
                  name="committee"
                  value={formData.committee}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select Committee</option>
                  <option value="PR">PR</option>
                  <option value="Operations">Operations</option>
                  <option value="Internal">Internal</option>
                  <option value="External">External</option>
                  <option value="SASComm">SASComm (All Committees)</option>
                </Form.Select>
              </Form.Group>
            </Form>
          </Card.Body>
        </Card>
      </div>
    );
  }

  // Render view mode
  return (
    <Container>
      {loading ? (
        <div className="text-center py-5 lotus-loader">
          <div className="lotus-petals">
            <LotusIcon size={32} />
          </div>
          <p className="mt-3">Loading meeting details...</p>
        </div>
      ) : error ? (
        <Alert variant="danger">{error}</Alert>
      ) : (
        <>
          <div className="section-title d-flex justify-content-between align-items-center mb-4">
            <div className="d-flex align-items-center">
              <LotusIcon className="me-3" size={32} />
              <h1 className="mb-0">{meeting.title}</h1>
            </div>
            <div>
              <Button
                variant="outline-primary"
                onClick={() => setIsEditing(true)}
                className="me-2"
                style={{ borderColor: "#f5c2c2", color: "#d9669b" }}
              >
                <FontAwesomeIcon icon={faEdit} className="me-2" />
                Edit
              </Button>
              <Button
                variant="outline-danger"
                onClick={() => setShowDeleteModal(true)}
                style={{ borderColor: "#ffcccb" }}
              >
                <FontAwesomeIcon icon={faTrashAlt} className="me-2" />
                Delete
              </Button>
            </div>
          </div>

          <Row>
            <Col lg={4}>
              <Card className="shadow-sm border-0 mb-4">
                <Card.Header
                  style={{
                    background: "linear-gradient(to right, #fff0f5, #fff8dc)",
                  }}
                >
                  <h5 className="mb-0">Meeting Information</h5>
                </Card.Header>
                <Card.Body>
                  <div className="meeting-info mb-3">
                    <div className="d-flex align-items-center mb-3">
                      <FontAwesomeIcon
                        icon={faCalendarAlt}
                        className="me-3"
                        style={{ color: "#d9669b", width: "20px" }}
                      />
                      <div>
                        <div className="text-muted small">Date & Time</div>
                        <div>{formatDate(meeting.date)}</div>
                      </div>
                    </div>

                    <div className="d-flex align-items-center mb-3">
                      <FontAwesomeIcon
                        icon={faClock}
                        className="me-3"
                        style={{ color: "#d9669b", width: "20px" }}
                      />
                      <div>
                        <div className="text-muted small">Duration</div>
                        <div>{meeting.duration} minutes</div>
                      </div>
                    </div>

                    <div className="d-flex align-items-center mb-3">
                      <FontAwesomeIcon
                        icon={faMapMarkerAlt}
                        className="me-3"
                        style={{ color: "#d9669b", width: "20px" }}
                      />
                      <div>
                        <div className="text-muted small">Location</div>
                        <div>{meeting.location || "No location specified"}</div>
                      </div>
                    </div>

                    <div className="d-flex align-items-center mb-3">
                      <Badge
                        bg={getCommitteeBadge(meeting.committee)}
                        className="sasc-badge"
                        style={{ minWidth: "100px", textAlign: "center" }}
                      >
                        {typeof meeting.committee === "object"
                          ? meeting.committee.name
                          : meeting.committee || "No Committee"}
                      </Badge>
                    </div>
                  </div>

                  <div className="meeting-description">
                    <h6 className="mb-2">Description</h6>
                    <p className="text-muted small">
                      {meeting.description || "No description provided."}
                    </p>
                  </div>
                </Card.Body>
              </Card>

              {/* FileAttachments component placed right after Meeting Information */}
              <FileAttachments meetingId={id} />

              {/* Attendance Card */}
              <Card
                className="shadow-sm border-0 mb-4"
                style={{ borderRadius: "12px", overflow: "hidden" }}
              >
                <Card.Header
                  className="d-flex justify-content-between align-items-center"
                  style={{
                    background: "linear-gradient(to right, #fff0f5, #fff8dc)",
                    cursor: "pointer",
                  }}
                  onClick={() => setShowAttendance(!showAttendance)}
                >
                  <h5 className="mb-0">
                    <FontAwesomeIcon
                      icon={faUserCheck}
                      className="me-2"
                      style={{ color: "#d9669b" }}
                    />
                    Attendance
                  </h5>
                  <FontAwesomeIcon
                    icon={showAttendance ? faChevronUp : faChevronDown}
                    style={{ color: "#d9669b" }}
                  />
                </Card.Header>
                <Collapse in={showAttendance}>
                  <div>
                    <Card.Body>
                      {loadingUsers ? (
                        <div className="text-center py-3">
                          <Spinner animation="border" size="sm" />
                          <span className="ms-2">Loading attendance...</span>
                        </div>
                      ) : !attendanceList || attendanceList.length === 0 ? (
                        <p className="text-muted small mb-0">
                          No members found for this committee.
                        </p>
                      ) : (
                        <ListGroup variant="flush">
                          {attendanceList.map((attendee, index) => (
                            <ListGroup.Item
                              key={index}
                              className="d-flex justify-content-between align-items-center px-0 py-2 border-bottom"
                            >
                              <div className="d-flex align-items-center">
                                <div
                                  style={{ width: "30px", textAlign: "center" }}
                                >
                                  {getStatusIcon(attendee.status)}
                                </div>
                                <div className="ms-2">
                                  {attendee.name || "Unknown Member"}
                                </div>
                              </div>
                              <div className="d-flex">
                                <OverlayTrigger
                                  placement="top"
                                  overlay={<Tooltip>Present</Tooltip>}
                                >
                                  <Button
                                    variant={
                                      attendee.status === "present"
                                        ? "success"
                                        : "outline-success"
                                    }
                                    size="sm"
                                    className="me-1"
                                    onClick={() =>
                                      handleAttendanceStatusChange(
                                        attendee.userId,
                                        "present"
                                      )
                                    }
                                    disabled={!attendee.userId}
                                  >
                                    <FontAwesomeIcon icon={faCheck} />
                                  </Button>
                                </OverlayTrigger>

                                <OverlayTrigger
                                  placement="top"
                                  overlay={<Tooltip>Late</Tooltip>}
                                >
                                  <Button
                                    variant={
                                      attendee.status === "late"
                                        ? "warning"
                                        : "outline-warning"
                                    }
                                    size="sm"
                                    className="me-1"
                                    onClick={() =>
                                      handleAttendanceStatusChange(
                                        attendee.userId,
                                        "late"
                                      )
                                    }
                                    disabled={!attendee.userId}
                                  >
                                    <FontAwesomeIcon icon={faUserClock} />
                                  </Button>
                                </OverlayTrigger>

                                <OverlayTrigger
                                  placement="top"
                                  overlay={<Tooltip>Absent</Tooltip>}
                                >
                                  <Button
                                    variant={
                                      attendee.status === "absent"
                                        ? "danger"
                                        : "outline-danger"
                                    }
                                    size="sm"
                                    className="me-1"
                                    onClick={() =>
                                      handleAttendanceStatusChange(
                                        attendee.userId,
                                        "absent"
                                      )
                                    }
                                    disabled={!attendee.userId}
                                  >
                                    <FontAwesomeIcon icon={faUserXmark} />
                                  </Button>
                                </OverlayTrigger>
                              </div>
                            </ListGroup.Item>
                          ))}
                        </ListGroup>
                      )}
                    </Card.Body>
                  </div>
                </Collapse>
              </Card>

              {/* Related Tasks Card */}
              <Card
                className="shadow-sm border-0 mb-4"
                style={{ borderRadius: "12px", overflow: "hidden" }}
              >
                <Card.Header
                  className="d-flex justify-content-between align-items-center"
                  style={{
                    background: "linear-gradient(to right, #fff0f5, #fff8dc)",
                  }}
                >
                  <h5 className="mb-0">
                    <FontAwesomeIcon
                      icon={faTasks}
                      className="me-2"
                      style={{ color: "#d9669b" }}
                    />
                    Related Tasks
                  </h5>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => setShowTaskModal(true)}
                    style={{ borderColor: "#f5c2c2", color: "#d9669b" }}
                  >
                    <FontAwesomeIcon icon={faLink} className="me-1" />
                    Attach Task
                  </Button>
                </Card.Header>
                <Card.Body>
                  {loadingTasks ? (
                    <div className="d-flex align-items-center">
                      <Spinner animation="border" size="sm" />
                      <span className="ms-2">Loading tasks...</span>
                    </div>
                  ) : attachedTasks.length === 0 ? (
                    <p className="text-muted mb-0">
                      No tasks attached to this meeting.
                    </p>
                  ) : (
                    <ListGroup variant="flush">
                      {attachedTasks.map((task) => (
                        <ListGroup.Item
                          key={task._id}
                          className="d-flex justify-content-between align-items-center"
                          action
                          onClick={() => handleOpenTaskDetail(task._id)}
                          style={{ cursor: "pointer" }}
                        >
                          <div className="d-flex align-items-center">
                            <Badge
                              bg={getStatusBadgeColor(task.status)}
                              className="me-2"
                            >
                              {task.status}
                            </Badge>
                            {task.title}
                          </div>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation(); // Prevent triggering the ListGroup.Item onClick
                              handleRemoveTask(task._id);
                            }}
                          >
                            <FontAwesomeIcon icon={faTimes} />
                          </Button>
                        </ListGroup.Item>
                      ))}
                    </ListGroup>
                  )}
                </Card.Body>
              </Card>
            </Col>

            <Col lg={8}>
              <Card
                className="shadow-sm border-0 mb-4 notes-card"
                style={{
                  borderRadius: "12px",
                  overflow: "hidden",
                  display: "flex",
                  flexDirection: "column",
                  height: "calc(100vh - 160px)",
                }}
              >
                <Card.Header
                  style={{
                    background: "linear-gradient(to right, #fff0f5, #fff8dc)",
                  }}
                >
                  <div className="d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">
                      <LotusIcon size={16} className="me-2" />
                      Meeting Notes
                    </h5>
                    <div className="d-flex align-items-center">
                      <div className="connection-status-container me-2">
                        {renderConnectionStatus()}
                      </div>
                      {isSaving && (
                        <span className="text-muted small me-2">
                          <FontAwesomeIcon
                            icon={faSpinner}
                            spin
                            className="me-1"
                          />
                          Saving...
                        </span>
                      )}
                      {lastSaved && (
                        <span className="text-muted small">
                          Last saved: {formatLastSaved()}
                        </span>
                      )}
                    </div>
                  </div>
                </Card.Header>
                <Card.Body
                  style={{
                    padding: "0",
                    display: "flex",
                    flexDirection: "column",
                    flex: "1 1 auto",
                    overflow: "hidden",
                  }}
                >
                  {activeUsers.length > 0 && (
                    <div className="px-3 pt-3 mb-2">
                      <div className="d-flex flex-wrap align-items-center">
                        <div className="me-2 text-muted small">
                          Active users:
                        </div>
                        {activeUsers.map((user) => (
                          <Badge
                            key={user.userId}
                            bg="light"
                            text="dark"
                            className="me-1 mb-1 sasc-badge"
                            style={{ border: "1px solid #f5c2c2" }}
                          >
                            <FontAwesomeIcon
                              icon={faUserCircle}
                              className="me-1"
                              style={{ color: "#d9669b" }}
                            />
                            {user.userName || "Anonymous"}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  <div
                    style={{
                      flex: "1",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <QuillEditor
                      value={notes}
                      onChange={handleNotesChange}
                      onBlur={handleManualSaveNotes}
                      readOnly={false}
                      meetingId={id}
                      connectionStatus={connectionStatus}
                      formats={quillFormats}
                    />
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Delete Meeting Modal */}
          <Modal
            show={showDeleteModal}
            onHide={() => setShowDeleteModal(false)}
          >
            <Modal.Header
              closeButton
              style={{
                background: "linear-gradient(to right, #fff0f5, #fff8dc)",
              }}
            >
              <Modal.Title style={{ color: "#d9669b" }}>
                Delete Meeting
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <p>Are you sure you want to delete this meeting?</p>
              <p className="text-danger">This action cannot be undone!</p>
            </Modal.Body>
            <Modal.Footer>
              <Button
                variant="outline-secondary"
                onClick={() => setShowDeleteModal(false)}
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={handleDelete}
                style={{ background: "#e66b8f", border: "none" }}
              >
                {isDeleting ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faTrashAlt} className="me-2" />
                    Delete
                  </>
                )}
              </Button>
            </Modal.Footer>
          </Modal>

          {/* Task Modal */}
          <Modal show={showTaskModal} onHide={() => setShowTaskModal(false)}>
            <Modal.Header closeButton>
              <Modal.Title>Attach Task to Meeting</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              {loadingTasks ? (
                <div className="text-center py-3">
                  <Spinner animation="border" size="sm" />
                  <span className="ms-2">Loading tasks...</span>
                </div>
              ) : availableTasks.length === 0 ? (
                <p>
                  No tasks available. Create tasks from the Tasks page first.
                </p>
              ) : (
                <ListGroup>
                  {availableTasks
                    .filter(
                      (task) =>
                        !attachedTasks.some(
                          (attached) => attached._id === task._id
                        )
                    )
                    .map((task) => (
                      <ListGroup.Item
                        key={task._id}
                        action
                        onClick={() => {
                          handleAttachTask(task._id);
                          setShowTaskModal(false);
                        }}
                        className="d-flex justify-content-between align-items-center"
                      >
                        <div>
                          <Badge
                            bg={getStatusBadgeColor(task.status)}
                            className="me-2"
                          >
                            {task.status}
                          </Badge>
                          <span>{task.title}</span>
                        </div>
                        <Badge
                          bg={getPriorityBadgeColor(task.priority)}
                          text={task.priority === "Low" ? "dark" : "white"}
                        >
                          {task.priority}
                        </Badge>
                      </ListGroup.Item>
                    ))}
                </ListGroup>
              )}
            </Modal.Body>
            <Modal.Footer>
              <Button
                variant="secondary"
                onClick={() => setShowTaskModal(false)}
              >
                Close
              </Button>
            </Modal.Footer>
          </Modal>

          {/* Task Detail Modal */}
          <Modal
            show={showTaskDetailModal}
            onHide={() => setShowTaskDetailModal(false)}
            size="lg"
            centered
          >
            <Modal.Header
              closeButton
              style={{
                background: "linear-gradient(to right, #fff0f5, #fff8dc)",
              }}
            >
              <Modal.Title style={{ color: "#d9669b" }}>
                <FontAwesomeIcon icon={faTasks} className="me-2" />
                Task Details
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              {loadingTaskDetails ? (
                <div className="text-center py-3">
                  <Spinner animation="border" role="status" variant="primary">
                    <span className="visually-hidden">Loading...</span>
                  </Spinner>
                </div>
              ) : !selectedTask ? (
                <Alert variant="warning">Task not found</Alert>
              ) : (
                <>
                  <h4 className="mb-3" style={{ color: "#d9669b" }}>
                    {selectedTask.title}
                  </h4>

                  <Row className="mb-4">
                    <Col md={6}>
                      <div className="mb-3">
                        <h6 className="text-muted">Status</h6>
                        <Badge
                          bg={getStatusBadgeColor(selectedTask.status)}
                          style={{ fontSize: "1rem", padding: "8px 12px" }}
                        >
                          {selectedTask.status}
                        </Badge>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="mb-3">
                        <h6 className="text-muted">Priority</h6>
                        <Badge
                          bg={getPriorityBadgeColor(selectedTask.priority)}
                          style={{ fontSize: "1rem", padding: "8px 12px" }}
                        >
                          {selectedTask.priority}
                        </Badge>
                      </div>
                    </Col>
                  </Row>

                  {selectedTask.description && (
                    <div className="mb-4">
                      <h6 className="text-muted">Description</h6>
                      <p>{selectedTask.description}</p>
                    </div>
                  )}

                  <Row className="mb-4">
                    <Col md={6}>
                      <div className="mb-3">
                        <h6 className="text-muted">Assigned To</h6>
                        <p>{selectedTask.assignedTo?.name || "Unassigned"}</p>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="mb-3">
                        <h6 className="text-muted">Due Date</h6>
                        <p>{formatTaskDate(selectedTask.dueDate)}</p>
                      </div>
                    </Col>
                  </Row>

                  {selectedTask.labels && selectedTask.labels.length > 0 && (
                    <div className="mb-4">
                      <h6 className="text-muted">Labels</h6>
                      <div className="d-flex flex-wrap">
                        {selectedTask.labels.map((label) => (
                          <Badge
                            key={label}
                            bg="light"
                            text="dark"
                            className="me-1 mb-1"
                            style={{ border: "1px solid #f5c2c2" }}
                          >
                            # {label}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {(selectedTask.relationships?.name ||
                    selectedTask.relatedTo?.name) && (
                    <div className="mb-4">
                      <h6 className="text-muted">Related To</h6>
                      <Badge
                        bg={
                          (selectedTask.relationships?.type ||
                            selectedTask.relatedTo?.type) === "meeting"
                            ? "info"
                            : (selectedTask.relationships?.type ||
                                selectedTask.relatedTo?.type) === "committee"
                            ? "warning"
                            : "success"
                        }
                        style={{ fontSize: "0.9rem", padding: "5px 10px" }}
                      >
                        <FontAwesomeIcon
                          icon={
                            (selectedTask.relationships?.type ||
                              selectedTask.relatedTo?.type) === "meeting"
                              ? faCalendarAlt
                              : (selectedTask.relationships?.type ||
                                  selectedTask.relatedTo?.type) === "committee"
                              ? faUsers
                              : faList
                          }
                          className="me-1"
                        />
                        {selectedTask.relationships?.name ||
                          selectedTask.relatedTo?.name}
                      </Badge>
                    </div>
                  )}
                </>
              )}
            </Modal.Body>
            <Modal.Footer>
              <Button
                variant="primary"
                onClick={() => handleEditTask(selectedTask)}
                style={{ background: "#d9669b", border: "none" }}
                className="me-2"
              >
                <FontAwesomeIcon icon={faEdit} className="me-2" />
                Edit Task
              </Button>
              <Button
                variant="secondary"
                onClick={() => setShowTaskDetailModal(false)}
              >
                Close
              </Button>
            </Modal.Footer>
          </Modal>

          {/* Task Edit Modal */}
          {showTaskEditModal && taskToEdit && (
            <TaskEditModal
              show={showTaskEditModal}
              onHide={() => setShowTaskEditModal(false)}
              task={taskToEdit}
              onTaskUpdated={handleTaskUpdated}
            />
          )}
        </>
      )}
    </Container>
  );
};

export default MeetingDetail;
